import type React from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../hooks/useAppContext";
import { CircularProgress } from "./custom/CircularScore";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { SCORE_THRESHOLDS } from "../services/detect";
import { Card, CardContent, CardFooter } from "./ui/card";
import { DetectDescription } from "./custom/DetectDescription";
import { OriginalText } from "./custom/OriginalText";
import { Accordion, AccordionContent, AccordionItem } from "./ui/accordion";
import {
	AccordionTitleScoreWithTrigger,
	AccordionTitleWithTrigger,
} from "./custom/AccordionTitleWithTrigger";
import { ResultPage } from "./custom/ResultPage";
import { trackEvent } from "../services/metrics";

const getScoreDescription = (score: number) => {
	if (score >= SCORE_THRESHOLDS.veryLikelyAi) return "Very Likely AI-Generated";
	if (score >= SCORE_THRESHOLDS.mostlyAi) return "Mostly AI-Generated";
	if (score >= SCORE_THRESHOLDS.mixed) return "Mixed Human and AI Content";
	if (score >= SCORE_THRESHOLDS.mostlyHuman) return "Mostly Human-Written";
	return "Very Likely Human-Written";
};

const ResultDetector: React.FC = () => {
	const navigate = useNavigate();
	const { detectResult } = useAppContext();

	const handleBack = () => {
		trackEvent({ event: "detect_again_clicked" });
		navigate("/");
	};

	return (
		<ResultPage onPressBack={handleBack}>
			<Accordion
				type="multiple"
				className="w-full h-full overflow-y-scroll"
				defaultValue={["item-1", "item-2"]}
			>
				<AccordionItem value="item-1">
					<AccordionTitleScoreWithTrigger
						title="AI Detection Score"
						score={detectResult.score}
					/>
					<AccordionContent className="flex flex-col items-center min-w-60 mx-3">
						<Card className="max-w-sm">
							<CardContent className="flex flex-col gap-3">
								<div className="px-6 min-w-36 max-w-52 mx-auto">
									<CircularProgress value={detectResult.score} />
								</div>
								<h2 className="text-lg font-semibold text-center pt-2">
									{getScoreDescription(detectResult.score)}
								</h2>
							</CardContent>
							<CardFooter>
								<DetectDescription value={detectResult.score} />
							</CardFooter>
						</Card>
					</AccordionContent>
				</AccordionItem>

				<AccordionItem value="item-2">
					<AccordionTitleWithTrigger
						title="Submitted Text"
						icon={<DocumentTextIcon className="size-4" />}
					/>
					<AccordionContent className="flex flex-col gap-4 text-balance">
						<OriginalText
							text={detectResult.text}
							indexes={detectResult.sentence_scores}
						/>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</ResultPage>
	);
};

export default ResultDetector;
